import { useState } from "react";
import { Bot, Loader2, Send } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useDispatchJob } from "@/hooks/useAgentJobs";
import { toast } from "sonner";
import type { Item } from "@/lib/types";
import { formatError } from "@/lib/utils";

type Props = {
  item: Item | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function DispatchSheet({ item, open, onOpenChange }: Props) {
  const dispatch = useDispatchJob();
  const [instructions, setInstructions] = useState("");

  if (!item) return null;

  const handleDispatch = () => {
    dispatch.mutate(
      { item_id: item.id, instructions: instructions.trim() || undefined },
      {
        onSuccess: () => {
          toast("Job dispatched to agent");
          setInstructions("");
          onOpenChange(false);
        },
        onError: (err) => toast.error(formatError(err)),
      }
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:w-3/4 sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-primary" />
            Dispatch to Agent
          </SheetTitle>
          <SheetDescription>
            The agent will work on this in an isolated branch and create a PR for your review.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-6">
          <div>
            <Label className="text-muted-foreground text-xs">Task</Label>
            <p className="text-sm font-medium mt-1">{item.title}</p>
          </div>

          {item.project_tag && (
            <div>
              <Label className="text-muted-foreground text-xs">Project</Label>
              <div className="mt-1">
                <Badge variant="secondary">{item.project_tag}</Badge>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="dispatch-instructions">Instructions (optional)</Label>
            <Textarea
              id="dispatch-instructions"
              placeholder="Add specifics... e.g. 'check the Vercel deploy logs, the build broke on the last push'"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={3}
            />
          </div>

          <Button
            onClick={handleDispatch}
            disabled={dispatch.isPending}
            className="w-full"
          >
            {dispatch.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Dispatch
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
